// FILECONTENT_START src/common/components/DataGrid/DataGrid.tsx
import { default as CancelIcon } from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Dialog,
  DialogProps,
  IconButtonProps,
  LinearProgress,
} from "@mui/material";
import {
  GridColumnHeaderParams,
  GridEnrichedColDef,
  GridRowModel,
} from "@mui/x-data-grid-pro";
import { Options } from "common/modules/helpers";
import { ReactNode, useEffect, useState } from "react";
import { appSettings } from "../../bootstrap";

import { SaveFormProps } from "./../Form";
import { UseFilterReturnType } from "./../List";
import Toolbar from "./../Toolbar";
import DataGridNoRowsOverlay from "./DataGridNoRowsOverlay";
import DataGridToolbar, {
  DataGridToolbarProps,
  HeaderItemTypes,
} from "./DataGridToolbar";

import { useDialog, UseDialogReturnProps } from "./../Dialog";
import Spacer from "./../Spacer";
import { DetailsGridRow } from "./DetailsGridRow";
import {
  GridActionsCellItem,
  GridColumns,
  GridOverlay,
  GridRowParams,
  GridRowProps,
  MuiDataGrid,
  MuiDataGridProps,
  useGridApiRef,
} from "./grid-imports";
import ActionButtonDelete from "./row-buttons/ActionButtonDelete";
import ActionButtonEdit from "./row-buttons/ActionButtonEdit";
import ActionButtonRestore from "./row-buttons/ActionButtonRestore";
import ActionButtonView from "./row-buttons/ActionButtonView";
import { GridRowsModel, TrashTypes } from "./useGrid";
import useGridInlineEdit from "./useGridInlineEdit";

export const renderIconHeader =
  ({ Icon }: { Icon: any }) =>
  (params: GridColumnHeaderParams) => {
    return (
      <Toolbar>
        <Icon fontSize="small" color="disabled" />
        <Spacer w={0.5} />
        <div
          style={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
        >
          {params.colDef.headerName}
        </div>
      </Toolbar>
    );
  };

export interface GridModalFormProps<T = any, L = any> {
  id?: string;
  listItem?: L;
  afterSave?: (newItem: T, isCreate: boolean) => void;
  onClose?: Function;
  setItem?: Function;
}

export interface DataGridActionButtonProps {
  onClick?: Function;
  onlyTrashed?: boolean;
  disabled?: boolean;
  to?: string;
}

function CustomLoadingOverlay() {
  return (
    <GridOverlay>
      <div style={{ position: "absolute", top: 0, width: "100%" }}>
        <LinearProgress />
      </div>
    </GridOverlay>
  );
}

export interface DataGridFilterProps<T = any, O = Options>
  extends UseFilterReturnType<T, O> {
  applyFilter: Function;
  resetFilter: Function;
}
export type GridColumnSettings = {
  [key: string]: Omit<GridEnrichedColDef, "field">;
};
export interface DataGridProps<
  T = any,
  L extends GridRowModel = any,
  F = any,
  O = any
> extends Omit<MuiDataGridProps, "onSelectionModelChange"> {
  canEdit?: boolean;
  canDelete?: boolean;
  canHandleTrash?: boolean;
  canBatchActions?: boolean;
  canCreate?: boolean;
  //loadItems(queryParams?: string, onlyTrashed?: boolean): Promise<L[]>;
  /*loadOptions?: (
    requestConfig?: AxiosRequestConfig,
    component?: string
  ) => Promise<O>;*/
  canEditRow?: (params: GridRowParams) => boolean;
  canDeleteRow?: (params: GridRowParams) => boolean;
  //deleteItem?(id: any, onlyTrashed?: boolean): Promise<SaveFormProps<T>>;
  //deleteItems?: (ids: any[], onlyTrashed?: boolean) => Promise<any>;
  //restoreItem?: (id: any) => Promise<SaveFormProps<T>>;
  restoreItems?: (ids: any[]) => Promise<any>;
  updateItem?(
    id: number,
    item: T,
    onlyTrashed?: boolean
  ): Promise<SaveFormProps<T>>;
  createItem?(item: T): Promise<SaveFormProps<T>>;
  onCreateItem?: (item: any) => void;
  onEditItem?: (item: GridRowParams<L>) => void;
  onViewItem?: (item: GridRowParams<L>) => void;
  editUrl?: string;
  rowActions?: (
    params: GridRowParams,
    openModal: Function
  ) => React.ReactElement[];
  headerItems?: HeaderItemTypes;
  DetailsComponent?: React.JSXElementConstructor<GridRowProps>;
  renderModalForm?: (props: GridModalFormProps) => ReactNode;
  refreshRef?: Function;
  extraHeaderItem?: (rowsState: GridRowsModel, filter: any) => ReactNode;
  headerTitle?: (rowsState: GridRowsModel) => ReactNode;
  renderFilter?: (props: DataGridFilterProps<F, O>) => ReactNode;
  renderSettings?: (props: DataGridFilterProps<F, O>) => ReactNode;
  pageSizeUrlParam?: string;
  pageUrlParam?: string;
  keywordUrlParam?: string;
  wrapCellText?: boolean;
  toolbarProps?: Partial<DataGridToolbarProps>;
  columnSettings?: GridColumnSettings;
  createTitle?: string;
  editTitle?: string;
  hideDisabledButtons?: boolean;
  onModalClosed?: () => void;
  ///-------------
  /*
  loading={loading}
        page={page}
        pageSize={pageSize}
        selectionModel={selectionModel}
        rows={rows}
        rowCount={rowCount}
        totals={totals}
        filterModel={filterModel}
        sortModel={sortModel}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={onSelectionModelChange}
        onFilterModelChange={onFilterModelChange}
        onSortModelChange={onSortModelChange}
  */
  loading?: boolean;
  load?: Function;
  deleteSelectedItems?: Function;
  restoreSelectedItems?: Function;
  prepareColumns?: Function;
  trashType?: TrashTypes;
  setTrashType?: Function;
  applyFilter?: Function;
  resetFilter?: Function;
  filterOpen?: boolean;
  autoColorFilterIndicator?: boolean;
  setFilterOpen?: Function;
  deleteItem?: Function;
  restoreItem?: Function;
  editDialogHook?: UseDialogReturnProps;
  buttonSize?: IconButtonProps["size"];
  modalProps?: Omit<DialogProps, "open" | "onClose" | "children">;
  canView?: boolean;
  renderFilterIndicatorItem?: (props: {
    key: string;
    field: string;
    fieldName?: string;
    label?: string;
    color?: string;
    backgroundColor?: string;
    deleteItem: Function;
    option: any;
  }) => ReactNode;
  ///-------------
}

export default function DataGrid<
  T = any,
  L extends GridRowModel = any,
  F = any,
  O = any
>({
  columns: _columns,
  editUrl,
  restoreItems,
  updateItem,
  createItem,
  rowActions,
  headerItems,
  canEdit,
  canDelete,
  canHandleTrash,
  canCreate,
  canEditRow,
  canDeleteRow,
  DetailsComponent,
  onCreateItem,
  onEditItem,
  onViewItem,
  renderModalForm,
  refreshRef,
  canBatchActions,
  extraHeaderItem,
  headerTitle,
  pageSizeUrlParam,
  pageUrlParam,
  keywordUrlParam,
  renderFilter,
  renderSettings,
  wrapCellText: _wrapCellText,
  loading,
  load,
  deleteSelectedItems,
  restoreSelectedItems,
  prepareColumns,
  trashType,
  setTrashType,
  applyFilter,
  resetFilter,
  filterOpen,
  setFilterOpen,
  deleteItem,
  restoreItem,
  toolbarProps,
  columnSettings,
  createTitle,
  editTitle,
  autoColorFilterIndicator,
  editDialogHook,
  buttonSize,
  modalProps,
  canView = true,
  hideDisabledButtons,
  renderFilterIndicatorItem,
  onModalClosed,
  ...props
}: DataGridProps<T, L, F, O>) {
  const apiRef = useGridApiRef();
  const dialog = useDialog<"create" | L>();

  const [modalId, setModalId, modal] = editDialogHook ? editDialogHook : dialog;

  const {
    classes,
    handleRowEditStart,
    handleRowEditStop,
    handleCellFocusOut,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
  } = useGridInlineEdit({ apiRef, updateItem, createItem });

  const [wrapCellText, setWrapCellText] = useState<boolean>(
    _wrapCellText || false
  );

  let columns = [];

  if (columnSettings) {
    columns = _columns.map(c => ({
      ...c,
      ...columnSettings[c.field],
    }));
  } else {
    columns = _columns;
  }

  useEffect(() => {
    setWrapCellText(_wrapCellText || false);
  }, [_wrapCellText]);

  function getRowActions(rowProps: GridRowParams<L>): React.ReactElement[] {
    const id = rowProps.id;
    let actions =
      rowActions && trashType !== "onlyTrashed"
        ? rowActions(rowProps, setModalId)
        : [];

    const isInEditMode =
      apiRef.current.getRowMode && apiRef.current.getRowMode(id) === "edit";

    if (isInEditMode) {
      return [
        <GridActionsCellItem
          icon={<SaveIcon />}
          label="Save"
          onClick={handleSaveClick(id)}
          //color="primary"
        />,
        <GridActionsCellItem
          icon={<CancelIcon />}
          label="Cancel"
          //className={classes.textPrimary}
          onClick={handleCancelClick(id)}
          //color="inherit"
        />,
      ];
    }
    if (props.editMode === "row" && canEdit) {
      const _canEdit = canEditRow ? canEditRow(rowProps) : true;
      //if (_canEdit) {
      actions.push(
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          //className={classes.textPrimary}
          onClick={handleEditClick(id)}
          disabled={!_canEdit}
          //color="inherit"
        />
      );
      //}
    }

    if (editUrl && props.editMode !== "row" && canEdit && !trashType) {
      const _canEdit = canEditRow ? canEditRow(rowProps) : true;
      if (_canEdit) {
        actions.push(
          <ActionButtonEdit to={editUrl.replace(":id", id as string)} />
        );
      } else {
        if (editUrl) {
          actions.push(
            <ActionButtonView to={editUrl.replace(":id", id as string)} />
          );
        }
      }
    } else if (onEditItem && canEdit && !trashType) {
      actions.push(<ActionButtonEdit onClick={() => onEditItem(rowProps)} />);
    } else if (renderModalForm && canEdit && !trashType) {
      const _canEdit = canEditRow ? canEditRow(rowProps) : true;
      if (canView) {
        if (_canEdit) {
          actions.push(
            <ActionButtonEdit
              onClick={() => setModalId && setModalId(rowProps.row)}
              //disabled={!_canEdit}
            />
          );
        } else {
          actions.push(
            <ActionButtonView
              onClick={() => setModalId && setModalId(rowProps.row)}
              //disabled={!_canEdit}
            />
          );
        }
      } else {
        actions.push(
          <ActionButtonEdit
            onClick={() => setModalId && setModalId(rowProps.row)}
            disabled={!_canEdit}
          />
        );
      }
    } else if ((onViewItem || canView) && !trashType) {
      if (onViewItem) {
        actions.push(<ActionButtonView onClick={() => onViewItem(rowProps)} />);
      } else {
        if (editUrl) {
          actions.push(
            <ActionButtonView to={editUrl.replace(":id", id as string)} />
          );
        } else {
          actions.push(
            <ActionButtonView
              onClick={() => setModalId && setModalId(rowProps.row)}
            />
          );
        }
      }
    } else if (editUrl && props.editMode !== "row" && !trashType) {
      actions.push(
        <ActionButtonView to={editUrl.replace(":id", id as string)} />
      );
    }
    const _canDelete = canDeleteRow ? canDeleteRow(rowProps) : true;
    if (restoreItem) {
      if (rowProps.row?.deleted_at && canHandleTrash) {
        actions.push(
          <ActionButtonRestore
            onClick={() => {
              restoreItem(rowProps.row);
            }}
          />
        );
      }
    }

    if (deleteItem && canDelete) {
      if (!hideDisabledButtons || (hideDisabledButtons && _canDelete)) {
        actions.push(
          <ActionButtonDelete
            onlyTrashed={!!rowProps.row?.deleted_at}
            onClick={() => {
              deleteItem(rowProps.row);
            }}
            disabled={!_canDelete}
          />
        );
      }
    }

    return actions;
  }
  const hasActions =
    editUrl || canEdit || canDelete || rowActions || onViewItem || canView;
  let gridColumns: GridColumns = prepareColumns
    ? prepareColumns(columns, hasActions ? getRowActions : undefined)
    : columns;

  let wrapCellTextProps = {};
  if (wrapCellText) {
    wrapCellTextProps = {
      className: `${classes.root} ${classes.wrapCellTextRoot}`,
      disableVirtualization: true,
      style: { minHeight: 240 },
    };
  }

  return (
    <Box>
      <MuiDataGrid
        className={classes.root}
        {...wrapCellTextProps}
        localeText={appSettings.i18n.gridLocaleText}
        columns={gridColumns}
        components={{
          LoadingOverlay: CustomLoadingOverlay,
          Toolbar: DataGridToolbar,
          NoRowsOverlay: DataGridNoRowsOverlay,
          Row: DetailsGridRow,
          //NoResultsOverlay: DataGridNoRowsOverlay,
          //Header: ToolBar
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            buttonSize,
            autoColorFilterIndicator,
            headerItems,
            canCreate,
            canBatchActions,
            canHandleTrash,
            editUrl,
            setWrapCellText,
            wrapCellText,
            extraHeaderItem,
            headerTitle,
            renderFilter,
            renderSettings,
            renderFilterIndicatorItem,
            deleteSelectedItems:
              canDelete && canBatchActions ? deleteSelectedItems : undefined,
            restoreSelectedItems:
              canHandleTrash && canBatchActions
                ? restoreSelectedItems
                : undefined,
            trashType,
            setTrashType,
            onCreate: renderModalForm
              ? () => setModalId && setModalId("create")
              : onCreateItem
              ? onCreateItem
              : undefined,
            apiRef, //Editing
          },
          row: {
            DetailsComponent,
          },
        }}
        autoHeight
        rowsPerPageOptions={[10, 20, 50, 100]}
        pagination
        loading={loading}
        paginationMode={load ? "server" : "client"}
        filterMode={load ? "server" : "client"}
        sortingMode={load ? "server" : "client"}
        apiRef={apiRef}
        //prevent default edit handler events
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        onCellFocusOut={handleCellFocusOut}
        checkboxSelection={canBatchActions}
        disableSelectionOnClick={canBatchActions}
        disableColumnFilter
        disableColumnMenu
        getRowId={
          (!props.rows[0] || !props.rows[0].id) && !props.getRowId
            ? row => JSON.stringify(row)
            : undefined
        }
        disableVirtualization
        {...props}
      />
      {!!renderModalForm && (
        <Dialog
          {...modal}
          onClose={(evt, reason) => {
            onModalClosed && onModalClosed();
            modal.onClose && modal.onClose(evt, reason);
          }}
          {...modalProps}
          fullWidth
        >
          {/*<DialogTitle onClose={modal.onClose}>
            {modalId === "create"
              ? createTitle || I18n.t("App.create")
              : editTitle || I18n.t("App.edit")}
          </DialogTitle>
          <DialogContent>
            {renderModalForm({
              id: modalId === "create" ? modalId : (modalId as any)?.id,
              listItem: modalId === "create" ? {} : modalId,
              afterSave: (id: any) => {
                setModalId(undefined);
                load && load();
              },
            })}
          </DialogContent>*/}
          {renderModalForm({
            id:
              modalId === "create"
                ? modalId
                : props.getRowId
                ? props.getRowId((modalId as any) || {})
                : (modalId as any)?.id,
            listItem: modalId === "create" ? {} : modalId,
            onClose: () => {
              onModalClosed && onModalClosed();
              modal.onClose && modal.onClose({} as any, "backdropClick");
            },
            setItem: (newItem: any) => {
              if (newItem) {
                setModalId(newItem);
              }
            },
            afterSave: (newItem: any, isCreate?: boolean) => {
              //setModalId(undefined);
              modal.onClose && modal.onClose({} as any, "backdropClick");
              load && load();
            },
          })}
        </Dialog>
      )}
    </Box>
  );
}

// FILECONTENT_END src/common/components/DataGrid/DataGrid.tsx
